import { CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Item, Member, Priority, Status } from '@/features/backlog/types';
import { PRIORITIES, priorityMeta, taskTypeMeta, SOURCES } from '@/features/backlog/constants';
import { useStatuses, useStatusMeta } from '@/features/backlog/statuses';
import { useEpicDropTarget, epicFocusMatches } from '@/features/backlog/epics';
import { fmtDate } from '@/features/backlog/format';
import { ColKey, ColumnDef } from '@/pages/board/columns';
import EpicChip from '@/components/epic-chip';
import { Badge, Tone } from '@/components/ui/Badge';
import { inputCls } from '@/components/ui/form';
import { DatePicker } from '@/components/ui/DatePicker';
import { Popover, menuRow } from '@/components/ui/Popover';
import { SelectOption, SelectPopover } from '@/components/ui/SelectPopover';
import { cn } from '@/components/ui/cn';
import { ContextMenu, MenuEntry, useContextMenu } from '@/components/ui/ContextMenu';
import {
  Check,
  Chevron,
  Comment,
  Duplicate,
  Grip,
  InsertAbove,
  InsertBelow,
  OpenPanel,
  Plus,
  PriorityChevron,
  SubItem,
  Swap,
  TaskTypeGlyph,
  ToBottom,
  ToTop,
  Trash,
} from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import StatusDot from '@/components/status-dot';
import { useMemberCreate } from '@/components/new-member';
import { ItemTab } from '@/components/item-drawer';
import { SELECTED_CLS } from '@/components/item-panel';

/** Left gutter (outside the table): holds the # rank + the 48x48 hover buttons. */
export const GUTTER = 'w-[128px]';
/** Gutter width in px — the table frame starts here so the gutter sits in the page margin. */
export const GUTTER_PX = 128;

export const INDENT = 22; // px per nesting level

const sourceTone = (s: string): Tone =>
  s === 'Customer' ? 'warn' : s === 'Dragos' ? 'accent' : s === 'Matt' ? 'violet' : 'neutral';

const stop = (e: ReactPointerEvent) => e.stopPropagation(); // keep editors from starting a row drag

export type EditTarget = { id: string; field: string } | null;

export type RowProps = {
  item: Item;
  rank: number;
  depth: number;
  hasChildren: boolean;
  collapsed: boolean;
  /** The visible data columns, in display order (Title is always rendered separately, ahead of these). */
  columns: ColumnDef[];
  /** CSS grid-template-columns string for the row card, matching the header so cells line up. */
  gridTemplate: string;
  /** Resolve an item's epic_id to the epic item, for the coloured chip above the key + title. */
  epicOf?: (id: string | null) => Item | undefined;
  /** Recede this row as filtered context (an ancestor/sibling kept only to frame a match). */
  dimmed?: boolean;
  members: Member[];
  memberMap: Map<string, Member>;
  editing: EditTarget;
  onEdit: (t: EditTarget) => void;
  onSave: (item: Item, patch: Partial<Item>) => void;
  onToggle: (id: string) => void;
  /** Open the item's drawer, optionally deep-linked to a tab (e.g. 'comments'). */
  onOpenDrawer: (id: string, tab?: ItemTab) => void;
  onAddSub: (parentId: string) => void;
  /** Insert a new sibling issue immediately above/below this one (same nesting level). */
  onAddSibling: (item: Item, where: 'above' | 'below') => void;
  /** Deep-duplicate this issue (with its subtree). */
  onDuplicate: (item: Item) => void;
  /** Move this item to the top/bottom of its sibling group (same parent), respecting nesting. */
  onReorderExtreme?: (item: Item, edge: 'top' | 'bottom') => void;
  /** Request deletion of this issue (opens a confirm dialog). */
  onDelete: (item: Item) => void;
  /** show the drag-handle cue (true when the row is inside a DndContext). */
  draggable?: boolean;
  /** true while shown in the DragOverlay. */
  overlay?: boolean;
  /** inert during a drag (e.g. a descendant of the item being dragged): renders in
   *  place but is neither draggable nor a drop target, so the subtree doesn't collapse. */
  disabled?: boolean;
  /** The item id to ring as "open in the details panel" — folded to the nearest visible
   *  ancestor when the selected item is hidden inside a collapsed branch (see visibleSelectedId). */
  highlightId?: string | null;
  /** Link this item to an epic dragged from the sidebar (native drop). Absent on inert copies. */
  onLinkEpic?: (itemId: string, epicId: string) => void;
  /** The epic being highlighted from the sidebar, so unlinked rows dim. */
  focusedEpicId?: string | null;
};

/** Sortable wrapper — the WHOLE row is the drag handle (listeners on the node). */
export default function SortableRow(props: RowProps) {
  const { item, disabled } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id, disabled });
  const style: CSSProperties = { transform: CSS.Translate.toString(transform), transition };
  return (
    <div
      ref={setNodeRef}
      style={style}
      // hover:z-30 (above the sticky header at z-25) so the first row's overhanging gutter controls
      // aren't clipped by the header; this wrapper is the page-level stacking context in tree mode.
      className={cn('group/row relative select-none hover:z-30', isDragging && 'opacity-40')}
      {...attributes}
      {...listeners}
    >
      <RowInner {...props} draggable />
    </div>
  );
}

export function RowInner(props: RowProps) {
  const { item, memberMap, editing, onEdit, onSave, overlay } = props;
  const { statuses } = useStatuses();
  const isEditing = (field: string) => editing?.id === item.id && editing.field === field;
  const activeMembers = [...props.members].filter((m) => m.active).sort((a, b) => a.name.localeCompare(b.name));
  // tap-target-y makes each cell button fill the 48px row height as its touch target.
  const cellBtn = 'tap-target-y w-full cursor-pointer rounded px-1.5 py-1 text-left transition-colors hover:bg-layer-strong';
  const cm = useContextMenu();
  // Ring the row while its item is the one open in the details panel — the same cue as the
  // context menu below (never in the drag overlay, which is a floating copy). The board folds
  // the id to a collapsed ancestor when the selected item is hidden, so this stays a plain compare.
  const isSelected = !overlay && props.highlightId != null && props.highlightId === item.id;

  // Epic-sidebar drop target (native HTML5 DnD — orthogonal to the row's @dnd-kit pointer drag).
  const epicDrop = useEpicDropTarget(item.id, props.onLinkEpic ?? (() => {}));
  const epicDropOver = !overlay && !!props.onLinkEpic && epicDrop.isOver;
  const epicDropColor = taskTypeMeta('epic').color;
  // Focus dimming: an epic is "focused" from the sidebar and this row isn't linked to it, OR the
  // board is filtered and this row is only kept as context (an ancestor/sibling of a match). Either
  // way it recedes so the real matches stand out.
  const focusDim = !overlay && props.focusedEpicId != null && !epicFocusMatches(item.epic_id, props.focusedEpicId);
  const recede = focusDim || (!overlay && !!props.dimmed);
  // The epic this item is linked to (for the coloured chip stacked above the key + title).
  const linkedEpic = props.epicOf && item.epic_id ? props.epicOf(item.epic_id) : undefined;

  // Click/tap anywhere on the row opens details — unless the click landed on an inline control
  // (cell editors, pickers, the gutter buttons) or is the click a finished drag emits (swallowed
  // by the board's capture handler).
  const onRowClick = (e: ReactMouseEvent) => {
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [role="menu"], [role="dialog"]')) return;
    props.onOpenDrawer(item.id);
  };

  return (
    <div
      // hover:z-30 lifts the hovered row ABOVE the sticky column header (z-25) so its gutter controls
      // are never clipped by it. `data-item-id` is the scroll target a shared `?item=` link centers on
      // (not on the drag overlay, which is a floating copy — it must not duplicate the id).
      className="group/row ctx-target relative flex items-stretch hover:z-30"
      {...(overlay ? {} : { 'data-item-id': item.id })}
      {...(overlay ? {} : { ...cm.triggerProps, onClick: onRowClick })}
      {...(overlay || !props.onLinkEpic ? {} : epicDrop.dropProps)}
    >
      {/* Left gutter — OUTSIDE the table: the # rank, replaced on hover by a compact control cluster
          that fits WITHIN the row height (no overhang), so adjacent rows' controls never overlap and
          the insert +'s are reachable without leaving the row. */}
      <div className={cn('relative flex shrink-0 items-center justify-end pr-2', GUTTER)}>
        <span className="tnum text-[12px] text-fg-faint transition-opacity group-hover/row:opacity-0">{props.rank}</span>
        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-stretch gap-0.5 py-1 opacity-0 transition-opacity group-hover/row:pointer-events-auto group-hover/row:opacity-100">
          {/* Insert above / below — a vertical pair that splits the row height, so the top half adds
              above and the bottom half adds below: a generous, in-row target for each. */}
          <div className="flex w-6 flex-col self-stretch">
            <button
              type="button"
              onPointerDown={stop}
              onClick={() => props.onAddSibling(item, 'above')}
              className="flex flex-1 items-center justify-center rounded-t-md text-fg-ghost transition-colors hover:bg-layer-strong hover:text-fg"
              aria-label="Add issue above"
              title="Add issue above"
            >
              <Plus />
            </button>
            <button
              type="button"
              onPointerDown={stop}
              onClick={() => props.onAddSibling(item, 'below')}
              className="flex flex-1 items-center justify-center rounded-b-md text-fg-ghost transition-colors hover:bg-layer-strong hover:text-fg"
              aria-label="Add issue below"
              title="Add issue below"
            >
              <Plus />
            </button>
          </div>
          {props.draggable ? (
            <span
              className="flex w-5 cursor-grab touch-none items-center justify-center self-stretch rounded-md text-fg-ghost transition-colors hover:bg-layer-strong hover:text-fg-soft active:cursor-grabbing"
              title="Drag anywhere on the row to reorder / nest"
              aria-hidden="true"
            >
              <Grip />
            </span>
          ) : null}
          <button
            type="button"
            onPointerDown={stop}
            onClick={() => props.onOpenDrawer(item.id)}
            className="flex w-7 items-center justify-center self-stretch rounded-md text-fg-ghost transition-colors hover:bg-layer-strong hover:text-fg"
            aria-label="Open details"
            title="Open details"
          >
            <OpenPanel />
          </button>
          <button
            type="button"
            onPointerDown={stop}
            onClick={() => props.onOpenDrawer(item.id, 'comments')}
            className="relative flex w-7 items-center justify-center self-stretch rounded-md text-fg-ghost transition-colors hover:bg-layer-strong hover:text-accent"
            aria-label="Open comments"
            title="Comments"
          >
            <span className="relative inline-flex">
              <Comment />
              {/* Dot only when the item actually has comments — an at-a-glance "has discussion" cue. */}
              {(item.comment_count ?? 0) > 0 ? (
                <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-accent" />
              ) : null}
            </span>
          </button>
        </div>
      </div>

      {/* Row card (inside the table frame) — clicking empty cell space opens the item. */}
      <div
        style={{
          gridTemplateColumns: props.gridTemplate,
          ...(epicDropOver ? { boxShadow: `inset 0 0 0 2px ${epicDropColor}` } : {}),
        }}
        className={cn(
          'grid min-h-[48px] flex-1 cursor-pointer items-center border-b border-line bg-surface-raised px-1 text-[13px] transition-colors group-hover/row:bg-layer dark:bg-transparent',
          // While its context menu is open, ring the row so you keep track of which one you
          // right-clicked. Inset so it stays within the row and doesn't bleed over the adjacent
          // rows' hairlines.
          cm.open && 'relative z-10 ring-2 ring-inset ring-accent',
          // Selected (open in the panel) but not right-clicked: the softer accent glow.
          !cm.open && isSelected && SELECTED_CLS,
          // An epic dragged from the sidebar is hovering this row (a drop links it).
          epicDropOver && 'relative z-10',
          // Focused-epic non-match, or filtered context row — recede so real matches stand out.
          recede && 'opacity-45',
        )}
      >
        {/* Title cell: optional epic chip stacked above the type glyph · key · inline title. */}
        <div className="flex min-w-0 flex-col justify-center gap-0.5 py-1 pr-2" style={{ paddingLeft: props.depth * INDENT }}>
          {linkedEpic && linkedEpic.id !== item.id ? (
            <div className="min-w-0 pl-12">
              <EpicChip epic={linkedEpic} />
            </div>
          ) : null}
          <div className="flex items-center gap-1">
          {props.hasChildren ? (
            <button
              type="button"
              onPointerDown={stop}
              onClick={() => props.onToggle(item.id)}
              className="tap-target-y flex h-5 w-5 shrink-0 items-center justify-center text-fg-faint hover:text-fg"
              aria-label={props.collapsed ? 'Expand' : 'Collapse'}
            >
              <Chevron className={cn('transition-transform', !props.collapsed && 'rotate-90')} />
            </button>
          ) : (
            <span className="w-5 shrink-0" />
          )}
          <span
            className="flex h-5 w-5 shrink-0 items-center justify-center"
            title={`Type: ${taskTypeMeta(item.type).label}`}
          >
            <TaskTypeGlyph type={item.type} color={taskTypeMeta(item.type).color} />
          </span>
          <span
            className="item-key shrink-0 rounded-md px-1.5 py-0.5 tnum text-[11px] font-semibold"
            title={item.key}
          >
            {item.key}
          </span>
          {isEditing('title') ? (
            <input
              className={cn(inputCls, 'h-8 py-1')}
              defaultValue={item.title}
              autoFocus
              onFocus={(e) => e.target.select()}
              onPointerDown={stop}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== item.title) onSave(item, { title: v });
                onEdit(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                if (e.key === 'Escape') onEdit(null);
              }}
            />
          ) : (
            <button type="button" className={cn(cellBtn, 'truncate font-medium text-fg')} onClick={() => onEdit({ id: item.id, field: 'title' })}>
              {item.title}
            </button>
          )}
          <button
            type="button"
            onPointerDown={stop}
            onClick={() => props.onAddSub(item.id)}
            className="tap-target-y ml-auto flex shrink-0 items-center rounded px-1 text-fg-ghost opacity-0 hover:bg-layer-strong hover:text-fg group-hover/row:opacity-100"
            title="Add sub-item"
            aria-label="Add sub-item"
          >
            +
          </button>
          </div>
        </div>

        {/* Configurable data columns — only the ones the member has enabled, in display order. */}
        {props.columns.map((col) => (
          <div
            key={col.key}
            className={cn('px-0.5', col.align === 'center' ? 'flex items-center justify-center' : 'min-w-0')}
          >
            <ColumnCell col={col.key} item={item} onSave={onSave} members={activeMembers} memberMap={memberMap} />
          </div>
        ))}
      </div>

      {!overlay && cm.open ? (
        <ContextMenu open x={cm.x} y={cm.y} onClose={cm.close} items={buildRowMenu(item, props, statuses)} />
      ) : null}
    </div>
  );
}

/** Right-click / long-press menu for a table row. */
function buildRowMenu(item: Item, props: RowProps, statuses: Status[]): MenuEntry[] {
  return [
    { label: 'Open details', icon: <OpenPanel className="h-4 w-4" />, onSelect: () => props.onOpenDrawer(item.id) },
    { label: 'Comments', icon: <Comment className="h-4 w-4" />, onSelect: () => props.onOpenDrawer(item.id, 'comments') },
    'separator',
    {
      label: 'Set status',
      icon: <Swap className="h-4 w-4" />,
      submenu: statuses.map((s) => ({
        label: s.label,
        icon: <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.color }} />,
        checked: item.status === s.value,
        onSelect: () => {
          if (item.status !== s.value) props.onSave(item, { status: s.value });
        },
      })),
    },
    {
      label: 'Set priority',
      icon: <Swap className="h-4 w-4" />,
      submenu: PRIORITIES.map((p) => ({
        label: p.label,
        icon: (
          <span className="flex h-4 w-4 items-center justify-center">
            <PriorityChevron level={p.level} color={p.color} />
          </span>
        ),
        checked: item.priority === p.value,
        onSelect: () => {
          if (item.priority !== p.value) props.onSave(item, { priority: p.value });
        },
      })),
    },
    'separator',
    // Reorder within the sibling group (respecting nesting), mirroring the kanban card menu.
    ...(props.onReorderExtreme
      ? ([
          { label: 'Bring to top', icon: <ToTop className="h-4 w-4" />, onSelect: () => props.onReorderExtreme!(item, 'top') },
          { label: 'Send to bottom', icon: <ToBottom className="h-4 w-4" />, onSelect: () => props.onReorderExtreme!(item, 'bottom') },
        ] as MenuEntry[])
      : []),
    'separator',
    { label: 'Add item above', icon: <InsertAbove className="h-4 w-4" />, onSelect: () => props.onAddSibling(item, 'above') },
    { label: 'Add item below', icon: <InsertBelow className="h-4 w-4" />, onSelect: () => props.onAddSibling(item, 'below') },
    { label: 'Add sub-item', icon: <SubItem className="h-4 w-4" />, onSelect: () => props.onAddSub(item.id) },
    'separator',
    { label: 'Duplicate', icon: <Duplicate className="h-4 w-4" />, onSelect: () => props.onDuplicate(item) },
    { label: 'Delete', icon: <Trash className="h-4 w-4" />, danger: true, onSelect: () => props.onDelete(item) },
  ];
}

/** Renders the correct inline editor for a single configurable table column. */
function ColumnCell(props: {
  col: ColKey;
  item: Item;
  onSave: (item: Item, patch: Partial<Item>) => void;
  members: Member[];
  memberMap: Map<string, Member>;
}) {
  const { col, item, onSave, members, memberMap } = props;
  switch (col) {
    case 'status':
      return <StatusCell item={item} onSave={onSave} />;
    case 'priority':
      return <PriorityCell item={item} onSave={onSave} />;
    case 'assignee':
      return (
        <MemberField value={item.assignee_id} members={members} memberMap={memberMap} onPick={(v) => onSave(item, { assignee_id: v })} />
      );
    case 'pdm':
      return <MemberField value={item.pdm_id} members={members} memberMap={memberMap} onPick={(v) => onSave(item, { pdm_id: v })} />;
    case 'source':
      return <SourceCell item={item} onSave={onSave} />;
    case 'labels':
      return <LabelsCell item={item} onSave={onSave} />;
    case 'start':
      return <DateField label="Start date" value={item.start_date} onPick={(v) => onSave(item, { start_date: v })} />;
    case 'end':
      return <DateField label="End date" value={item.end_date} onPick={(v) => onSave(item, { end_date: v })} />;
    default:
      return null;
  }
}

/** Status dot that opens the shared searchable picker (same control as the Kanban card / drawer). */
function StatusCell(props: { item: Item; onSave: (item: Item, patch: Partial<Item>) => void }) {
  const { item, onSave } = props;
  const { statuses } = useStatuses();
  const sm = useStatusMeta()(item.status);
  const options: SelectOption[] = statuses.map((s) => ({
    value: s.value,
    label: s.label,
    leading: <StatusDot value={s.value} size={16} />,
  }));
  return (
    <SelectPopover
      options={options}
      value={item.status}
      onChange={(v) => onSave(item, { status: v })}
      searchPlaceholder="Search statuses…"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tap-target flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-layer-strong"
          title={sm.label}
          aria-label={`Status: ${sm.label}`}
        >
          <StatusDot value={item.status} size={18} />
        </button>
      )}
    />
  );
}

/** Priority chevron that opens the shared searchable picker (same interaction as StatusCell). */
function PriorityCell(props: { item: Item; onSave: (item: Item, patch: Partial<Item>) => void }) {
  const { item, onSave } = props;
  const pm = priorityMeta(item.priority);
  const options: SelectOption[] = PRIORITIES.map((p) => ({
    value: p.value,
    label: p.label,
    leading: <PriorityChevron level={p.level} color={p.color} />,
  }));
  return (
    <SelectPopover
      options={options}
      value={item.priority}
      onChange={(v) => onSave(item, { priority: v as Priority })}
      searchPlaceholder="Search priority…"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tap-target flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-layer-strong"
          title={`Priority: ${pm.label}`}
          aria-label={`Priority: ${pm.label}`}
        >
          <PriorityChevron level={pm.level} color={pm.color} />
        </button>
      )}
    />
  );
}

/** Assignee / PdM avatar that opens the shared searchable member picker in one click. */
function MemberField(props: {
  value: string | null;
  members: Member[];
  memberMap: Map<string, Member>;
  onPick: (v: string | null) => void;
}) {
  const { value, members, memberMap, onPick } = props;
  const memberCreate = useMemberCreate();
  const options: SelectOption[] = [
    { value: '', label: 'Unassigned', leading: <MemberAvatar member={undefined} size={20} />, tone: 'text-fg-muted' },
    ...members.map((m) => ({
      value: m.id,
      label: m.name,
      leading: <MemberAvatar member={m} size={20} />,
      keywords: m.email ?? '',
    })),
  ];
  return (
    <SelectPopover
      options={options}
      value={value ?? ''}
      onChange={(v) => onPick(v || null)}
      {...memberCreate((id) => onPick(id))}
      searchPlaceholder="Search member…"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="tap-target flex items-center justify-center rounded-full p-0.5 transition-colors hover:bg-layer-strong"
          aria-label={`Member: ${memberMap.get(value ?? '')?.name ?? 'Unassigned'}. Change member`}
        >
          {/* ~24px avatar, but the tap-target overlay keeps a 48×48 touch zone. */}
          <MemberAvatar member={memberMap.get(value ?? '')} size={24} />
        </button>
      )}
    />
  );
}

/** Source badges that open a multi-select checklist. Stays open while toggling (multi-pick),
 *  closing on outside-click / Escape like the other cell menus. */
function SourceCell(props: { item: Item; onSave: (item: Item, patch: Partial<Item>) => void }) {
  const { item, onSave } = props;
  const selected = item.source ?? [];
  const options = Array.from(new Set([...SOURCES, ...selected]));
  const toggle = (s: string) =>
    onSave(item, { source: selected.includes(s) ? selected.filter((v) => v !== s) : [...selected, s] });
  return (
    <Popover
      trigger={({ toggle: open }) => (
        <button
          type="button"
          onClick={open}
          className="tap-target-y flex w-full flex-wrap gap-1 rounded px-1.5 py-1 text-left transition-colors hover:bg-layer-strong"
          aria-label="Set source"
        >
          {selected.length ? (
            selected.map((s) => (
              <Badge key={s} tone={sourceTone(s)}>
                {s}
              </Badge>
            ))
          ) : (
            <span className="text-fg-ghost">—</span>
          )}
        </button>
      )}
    >
      {() => (
        <>
          {options.map((s) => {
            const checked = selected.includes(s);
            return (
              <button key={s} type="button" onClick={() => toggle(s)} className={cn(menuRow, 'text-fg-soft')}>
                <span
                  className={cn(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    checked ? 'border-accent bg-accent text-black' : 'border-line',
                  )}
                >
                  {checked ? <Check className="h-3 w-3" /> : null}
                </span>
                <span className="flex-1">{s}</span>
              </button>
            );
          })}
          {selected.length ? (
            <button
              type="button"
              onClick={() => onSave(item, { source: [] })}
              className="mt-1 w-full rounded-lg border-t border-line px-2 pb-1 pt-2 text-left text-[12px] text-fg-faint transition-colors hover:text-fg-soft"
            >
              Clear
            </button>
          ) : null}
        </>
      )}
    </Popover>
  );
}

/** Label chips that open a quick add/remove menu with a create-in-place input. Labels are free-form +
 *  workspace-global; the full autocomplete lives in the drawer — this cell is for fast add/remove. */
function LabelsCell(props: { item: Item; onSave: (item: Item, patch: Partial<Item>) => void }) {
  const { item, onSave } = props;
  const [q, setQ] = useState('');
  const selected = item.labels ?? [];
  const add = (v: string) => {
    const t = v.trim();
    if (t && !selected.includes(t)) onSave(item, { labels: [...selected, t] });
    setQ('');
  };
  return (
    <Popover
      trigger={({ toggle: open }) => (
        <button
          type="button"
          onClick={open}
          className="tap-target-y flex w-full flex-wrap gap-1 rounded px-1.5 py-1 text-left transition-colors hover:bg-layer-strong"
          aria-label="Edit labels"
        >
          {selected.length ? selected.map((l) => <Badge key={l} tone="accent">{l}</Badge>) : <span className="text-fg-ghost">—</span>}
        </button>
      )}
    >
      {() => (
        <div className="w-[220px]">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Add a label…"
            aria-label="Add a label"
            className={cn(inputCls, 'mb-1 h-8')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add(q);
              }
            }}
          />
          {selected.map((l) => (
            <button key={l} type="button" onClick={() => onSave(item, { labels: selected.filter((x) => x !== l) })} className={cn(menuRow, 'text-fg-soft')}>
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded border border-accent bg-accent text-black">
                <Check className="h-3 w-3" />
              </span>
              <span className="flex-1 truncate">{l}</span>
            </button>
          ))}
          {q.trim() && !selected.includes(q.trim()) ? (
            <button type="button" onClick={() => add(q)} className={cn(menuRow, 'text-accent')}>
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                <Plus className="h-3 w-3" />
              </span>
              <span className="flex-1 truncate">Create “{q.trim()}”</span>
            </button>
          ) : null}
          {selected.length === 0 && !q.trim() ? <div className="px-2 py-3 text-center text-[12px] text-fg-ghost">No labels yet — type to add one.</div> : null}
        </div>
      )}
    </Popover>
  );
}

function DateField(props: { label: string; value: string | null; onPick: (v: string | null) => void }) {
  return (
    <DatePicker
      value={props.value}
      onChange={props.onPick}
      heading={props.label}
      align="right"
      trigger={({ toggle }) => (
        <button
          type="button"
          className="tap-target-y w-full cursor-pointer whitespace-nowrap rounded px-1.5 py-1 text-left text-fg-muted transition-colors hover:bg-layer-strong"
          onClick={toggle}
          onPointerDown={stop}
        >
          {fmtDate(props.value) || <span className="text-fg-ghost">—</span>}
        </button>
      )}
    />
  );
}
